import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import { DialogProps as MUIDialogProps } from "@mui/material/Dialog";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  MRT_TableInstance,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_Row,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import { MaskedTeam } from "work/_shared/masked-fields";
import FeeScheduleLink from "work/fee-schedule-templates/_views/links/fee-schedule-template-link";
import ProposalRequestDraftDialog from "work/proposal-requests/view/proposal-request-draft-dialog";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  HumanReadableProposalStatus,
  ProposalBoxType,
  ProposalOrderField,
  ProposalStatus,
} from "work/proposals/__constants/constants";
import {
  useBulkDeleteProposalsMutation,
  useCreateProposalMutation,
  useGetProposalByIdQuery,
  useGetProposalsQuery,
  useSaveProposalMutation,
  useShareProposalMutation,
} from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  addProposal,
  clearNewProposalBuilder,
  getLastSuccessfulProposalQueryByInterface,
  getOriginalProposalBuilderByProposalId,
  getProposalById,
  getProposalQueryByInterface,
  getProposalsByQuery,
  getUpdatedProposalBuilderByProposalId,
  isProposalLoadIssue,
  ProposalLoadIssue,
  resetBuilderByProposalId,
  resetProposalSessionByProposalId,
  updateProposalInterfaceQuery,
} from "work/proposals/_store/proposals-redux-slice";
import CreatorLink from "work/proposals/_views/_links/creator-link/creator-link";
import ProposalToolbar from "work/proposals/_views/_toolbars/proposal-toolbar";
import ProposalRedlineDialog from "work/proposals/_views/active/active-proposal-dialog";
import ArchivedProposalDialog from "work/proposals/_views/archive/archived-proposal-dialog";
import ProposalDraftDialog from "work/proposals/_views/drafts/draft-proposal-dialog";
import { PageTab } from "work/proposals/_views/proposals-page-tabs";
import {
  clearUnsavedCommentsByProposalId,
  getAreUnsavedComments,
  publishComments,
  saveComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import { ProposalFieldCategory } from "work/proposals/proposal-fields/proposal-field";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";

const interfaceName = "proposalsPage";

const ProposalToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ProposalToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const ProposalAlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));

export default function ProposalsPage() {
  const { numDialogs, openDialog, closeDialog, updateDialogProps } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const location = useLocation();

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const proposalId = routeParams.id ? new Guid(routeParams.id) : Guid.EMPTY;

  const proposalsQueryParams = useSelector(getProposalQueryByInterface(interfaceName));

  const paginatedProposalsJson = useSelector(getProposalsByQuery(proposalsQueryParams));
  const paginatedProposals = useMemo(() => {
    if (!paginatedProposalsJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const proposalJson of paginatedProposalsJson.data) {
      try {
        if (isProposalLoadIssue(proposalJson)) {
          deserializedData.push(proposalJson);
        } else {
          const proposal = Proposal.fromJSON({
            json: proposalJson,
            currentUser: session.user!,
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", proposalJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in proposalJson ? (Guid.fromJSON(proposalJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedProposalsJson, session.user?.id?.value]);

  const proposalsAPIQuery = useGetProposalsQuery(
    proposalsQueryParams ? { query: proposalsQueryParams, interfaceName } : skipToken,
  );
  const [saveProposalQuery] = useSaveProposalMutation();
  const [createProposalQuery] = useCreateProposalMutation();
  const [shareProposalQuery, shareProposalResult] = useShareProposalMutation();
  const [bulkDeleteProposalsQuery, bulkDeleteProposalsResult] = useBulkDeleteProposalsMutation();

  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulProposalQueryByInterface(interfaceName));
  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isProposalLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const proposal = Proposal.fromJSON({
            json: item,
            currentUser: session.user!,
            options: { createNewRedline: false },
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson, session.user?.id?.value]);

  const tableProposals = paginatedProposals ?? lastSuccessfulQueryResponse;

  const proposalByIdQuery = useGetProposalByIdQuery(
    proposalId && !proposalId.isEmpty ? { proposalId: proposalId.value, session } : skipToken,
  );
  const dialogProposalJson = useSelector(getProposalById(proposalId));
  const dialogProposal = useMemo(() => {
    if (!dialogProposalJson || isProposalLoadIssue(dialogProposalJson) || !session.user) return null;
    return Proposal.fromJSON({ json: dialogProposalJson, currentUser: session.user });
  }, [dialogProposalJson, session.user?.id?.value]);

  const originalBuilderJSON = useSelector(getOriginalProposalBuilderByProposalId(proposalId));
  const originalBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: originalBuilderJSON, session });
  }, [originalBuilderJSON, session.user?.id?.value]);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposalId));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: updatedBuilderJSON, session });
  }, [updatedBuilderJSON, session.user?.id?.value]);

  const updatedBuilderIssues = useMemo(() => {
    return updatedBuilder ? ProposalIssues.fromBuilder(updatedBuilder) : new ProposalIssues();
  }, [updatedBuilder?.currentIndex]);
  const updatedBuilderIssuesRef = React.useRef<ProposalIssues>(updatedBuilderIssues);
  useEffect(() => {
    updatedBuilderIssuesRef.current = updatedBuilderIssues;
  }, [updatedBuilderIssues]);

  const [activeTab, setActiveTab] = useState(PageTab.Active);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  const [activeDialogId, setActiveDialogId] = useState<Guid | undefined>();
  const [activeDialogProps, setActiveDialogProps] = useState<DialogProps>();
  const [isSaving, setIsSaving] = useState(false);

  const areUnsavedComments = useSelector(getAreUnsavedComments(dialogProposal));

  const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

  const dialogProposalRef = React.useRef(dialogProposal);
  const updatedBuilderRef = React.useRef(updatedBuilder);
  const areUnsavedCommentsRef = React.useRef(areUnsavedComments);
  const isDirtyRef = React.useRef<boolean>(false);
  useEffect(() => {
    dialogProposalRef.current = dialogProposal;
    updatedBuilderRef.current = updatedBuilder;
    areUnsavedCommentsRef.current = areUnsavedComments;
    if (!dialogProposal?.isSubmitted) {
      isDirtyRef.current = Boolean(updatedBuilder?.isDifferentFrom(originalBuilder) || areUnsavedComments);
    }
  }, [dialogProposal, updatedBuilder, areUnsavedComments]);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!activeDialogProps || !session?.user?.id || dialogProposal?.isSubmitted || dialogProposal?.supersedes) {
      return;
    }

    const disableSaveButton = !isDirtyRef.current || !updatedBuilderIssues.canSave;
    let tooltip = "Save changes (just for yourself).";
    if (!isDirtyRef.current) {
      tooltip = "No changes to save.";
    } else if (!updatedBuilderIssues.canSave) {
      tooltip = "Fill out all required fields to save.";
    }

    const nextDialogProps: DialogProps = {
      ...activeDialogProps,
      saveAbleItem: updatedBuilderRef.current,
      disableSave: disableSaveButton,
      saveButtonTooltip: tooltip,
      isDirty: isDirtyRef.current,
      onSave: handleSaveDraft,
    };

    setActiveDialogProps(nextDialogProps);
  }, [updatedBuilder]);

  useEffect(() => {
    if (!dialogProposal?.redline || !activeDialogId || !activeDialogProps) return;

    const isModified = Boolean(dialogProposal.isModified || areUnsavedComments);

    const nextDialogProps: DialogProps = {
      ...activeDialogProps,
      saveAbleItem: dialogProposal,
      disableSave: !isModified,
      saveButtonTooltip: isModified ? "Save changes (just for yourself)" : "No changes to save",
      isDirty: isModified,
    };

    const changed =
      nextDialogProps.saveAbleItem !== activeDialogProps.saveAbleItem ||
      nextDialogProps.disableSave !== activeDialogProps.disableSave ||
      nextDialogProps.saveButtonTooltip !== activeDialogProps.saveButtonTooltip ||
      nextDialogProps.isDirty !== activeDialogProps.isDirty;

    if (!changed) return;

    setActiveDialogProps(nextDialogProps);
  }, [dialogProposal?.redline?.sessionHistory.length, areUnsavedComments]);

  useEffect(() => {
    if (!activeDialogId || !activeDialogProps) return;
    updateDialogProps(activeDialogId, activeDialogProps);
  }, [activeDialogId, activeDialogProps]);

  useEffect(() => {
    if (!routeParams) return;
    if (routeParams.pageTab) {
      setActiveTab(routeParams.pageTab as PageTab);
    } else {
      navigate(`/proposals/${PageTab.Active}`);
    }
  }, [routeParams.pageTab]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    let boxType = searchParams.get("boxType") as ProposalBoxType | undefined;
    if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Active) boxType = ProposalBoxType.AllActive;
    else if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Drafts) boxType = ProposalBoxType.Draft;
    else if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Archived) boxType = ProposalBoxType.Archive;

    const filterParameters = ProposalFilterParameters.fromSearchParams(searchParams, session, boxType);
    const orderParameters = ProposalOrderParameters.fromSearchParams(searchParams);

    const newQuery = new ProposalQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (proposalsQueryParams && newQuery.isEqualTo(proposalsQueryParams)) return;
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [routeParams.pageTab, searchParams.toString(), activeTab, session.user?.legalEntityId?.value]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value as string | undefined;
    if (statusFilter) {
      newSearchParams.set("status", statusFilter);
    }

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) {
      newSearchParams.set("name", nameFilter);
    }

    const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
    if (descriptionFilter) {
      newSearchParams.set("description", descriptionFilter);
    }

    const creatorFilter = columnFilters.find((filter) => filter.id === "creator")?.value as string | undefined;
    if (creatorFilter) {
      newSearchParams.set("creator", creatorFilter);
    }

    const negotiableFilter = columnFilters.find((filter) => filter.id === "negotiable")?.value as string | undefined;
    if (negotiableFilter !== undefined) {
      newSearchParams.set("negotiable", negotiableFilter);
    }

    const orderingField = sorting.find((sort) =>
      Object.values(ProposalOrderField)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex;
    if (paginationPageIndex !== undefined) newSearchParams.set("page", (paginationPageIndex + 1).toString());

    const paginationPageSize = pagination.pageSize;
    if (paginationPageSize !== undefined) newSearchParams.set("pageSize", paginationPageSize.toString());

    if (newSearchParams.toString() === searchParams.toString()) return;

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination]);

  useEffect(() => {
    if (proposalsAPIQuery.isError) {
      enqueueSnackbar("Error loading proposal table", { variant: "error" });
    }
  }, [proposalsAPIQuery.isError]);

  useEffect(() => {
    if (
      proposalId.isEmpty ||
      dialogProposal !== null ||
      !routeParams ||
      proposalByIdQuery.isLoading ||
      routeParams.action === "create"
    )
      return;
    if (routeParams.action === "review" && routeParams.id) {
      enqueueSnackbar("You are no longer a reviewer on this proposal", {
        variant: "warning",
      });
    } else if (routeParams.id) {
      enqueueSnackbar("Proposal not found", { variant: "warning" });
    }
    navigate(`/proposals/${activeTab}`);
  }, [dialogProposal]);

  useEffect(() => {
    if (session.user?.id && dialogProposal?.createdDate) {
      const pageTab = routeParams.pageTab as PageTab;
      const action = routeParams.action as string;
      const reviewer = dialogProposal.getReviewer(session.user.id);

      redirectForProposalIfNecessary(action, reviewer?.entityMember, pageTab);
    }
  }, [dialogProposal, routeParams.pageTab, routeParams.action, session.user?.id?.value]);

  useEffect(() => {
    if (numDialogs > 0) return;
    if (!session.user || proposalsAPIQuery.isLoading) return;
    const action = routeParams.action as string;
    if (!action) return;
    const tab = ProposalFieldCategory[routeParams.tab as keyof typeof ProposalFieldCategory];
    openProposalDialog(action, tab);
  }, [proposalId?.value, proposalsAPIQuery.isLoading, routeParams.action, routeParams.tab]);

  function redirectForProposalIfNecessary(action: string, reviewer: EntityMember | undefined, pageTab: PageTab) {
    if (!dialogProposal || !routeParams || (proposalId.isEmpty && routeParams.action === "create")) return;
    let redirectAction: string | undefined = undefined;

    // If the proposal was replaced by another proposal, redirect to the new proposal
    if (dialogProposal.supersededById) {
      navigate(`/proposals/${pageTab}/${action}/${dialogProposal.supersededById.value}`, {
        replace: true,
        state: {
          previousProposalId: dialogProposal.id?.value,
          newProposalId: dialogProposal.supersededById.value,
        },
      });
      closeDialog();
      return;
    }

    // If the proposal hired, redirect to the work agreement
    if (dialogProposal.isHired && activeTab !== PageTab.Archived) {
      navigate(`/work/active/view/${dialogProposal.workAgreementId?.value}`, {
        replace: true,
      });
      return;
    }

    // If the proposal is submitted, redirect to the active proposals tab
    if (action === "edit" && dialogProposal.isSubmitted) {
      navigate("/proposals/active", { replace: true });
      return;
    }

    if (!dialogProposal.isArchived && reviewer && action !== "review") {
      redirectAction = "review";
    }
    if (dialogProposal.isArchived && action !== "view") {
      redirectAction = "view";
    }

    let redirectPageTab: PageTab | undefined = undefined;
    if (pageTab === PageTab.Drafts && dialogProposal.isSubmitted) {
      redirectPageTab = PageTab.Active;
    }
    if (pageTab === PageTab.Active && !dialogProposal.isSubmitted) {
      redirectPageTab = PageTab.Drafts;
    }
    if (dialogProposal.isArchived && pageTab !== PageTab.Archived) {
      redirectPageTab = PageTab.Archived;
    }

    if (redirectAction || redirectPageTab) {
      const newPath = `/proposals/${redirectPageTab ?? pageTab}/${redirectAction ?? action}/${proposalId}`;
      navigate(newPath, { replace: true });
    }
  }

  function openProposalDialog(action: string, _tab: ProposalFieldCategory) {
    if (!session.user || dialogProposal?.supersededById || !action) {
      console.warn("Unable to open proposal dialog. Missing required parameters.");
      return;
    }
    switch (action) {
      case "create":
        handleCreateDraftProposal();
        break;
      case "request":
        handleRequestProposal();
        break;
      case "edit":
        handleOpenDraftProposalDialog("Edit Draft Proposal", proposalId);
        break;
      case "review":
        if (proposalId && dialogProposal?.isSubmitted) {
          handleOpenActiveProposalDialog("Review Proposal", proposalId);
        } else if (dialogProposal) {
          handleOpenDraftProposalDialog("Review Draft Proposal", proposalId);
        }
        break;
      case "revise":
        if (proposalId) {
          handleOpenActiveProposalDialog("Revise Proposal", proposalId);
        }
        break;
      default:
        if (dialogProposal?.isArchived && proposalId) {
          handleOpenArchivedProposalDialog(proposalId);
        } else if (proposalId && dialogProposal?.isSubmitted) {
          handleOpenActiveProposalDialog("View Proposal", proposalId, true);
        } else if (dialogProposal) {
          handleOpenDraftProposalDialog("View Draft Proposal", proposalId, true);
        }
        break;
    }
  }

  function handleOpenArchivedProposalDialog(proposalId: Guid) {
    openDialog({
      title: "Archived Proposal",
      MuiProps: { fullWidth: false, maxWidth: "lg" },
      contentSxProps: { pb: { xs: 0 } },
      titleStyle: { paddingBottom: 0 },
      onClose: () => {
        navigate("/proposals/archived");
      },
      component: <ArchivedProposalDialog navigate={navigate} proposalId={proposalId} />,
    });
  }

  async function handleRequestProposal() {
    openDialog({
      title: "Request Proposal",
      resetStack: true,
      component: (
        <ProposalRequestDraftDialog
          onProposalSubmitted={(submittedProposal: Proposal) => {
            navigate(`/proposals/active/revise/${submittedProposal.id}`, {
              replace: true,
            });
            dispatch(addProposal(submittedProposal));
            closeDialog();
          }}
          onCancel={() => {
            navigate(parentPath.pathname);
            closeDialog();
          }}
        />
      ),
    });
  }

  function handleCreateDraftProposal() {
    const draftDialogProps: DialogProps = {
      title: "Create Proposal",
      MuiProps: {
        fullWidth: false,
        maxWidth: "lg",
      } as Partial<MUIDialogProps>,
      contentSxProps: { pb: { xs: 0 } },
      titleStyle: { paddingBottom: 0 },
      resetStack: true,
      onClose: handleCloseDraftDialog,
      isDirty: false,
      showSaveButton: true,
      disableSave: true,
      onSave: (saveAbleItem?: unknown, preventNavigation?: boolean) => {
        if (saveAbleItem && saveAbleItem instanceof ProposalBuilder) {
          return handleSaveDraft(preventNavigation);
        }
        throw new Error("Invalid proposal builder");
      },
      component: (
        <ProposalDraftDialog 
          isSaving={isSaving} 
          onSaveAndShareChanges={handleSaveAndShareChanges} 
        />
      ),
    };

    setActiveDialogProps(draftDialogProps);
    setActiveDialogId(openDialog(draftDialogProps));
  }

  function handleOpenDraftProposalDialog(title: string, proposalId?: Guid, disableEditing?: boolean) {
    if (!proposalId) {
      console.error("Proposal ID is required to edit a draft proposal.");
      return;
    }

    const draftDialogProps: DialogProps = {
      title: title,
      MuiProps: { fullWidth: false, maxWidth: "lg" },
      contentSxProps: { pb: { xs: 0 } },
      titleStyle: { paddingBottom: 0 },
      resetStack: true,
      onClose: handleCloseDraftDialog,
      isDirty: false,
      showSaveButton: true,
      disableSave: true,
      onSave: async () => {
        if (!updatedBuilder) {
          console.error("Nothing to save.");
          return;
        }

        await handleSaveDraft(Boolean(dialogProposal?.createdDate));
      },
      component: (
        <ProposalDraftDialog
          proposalId={proposalId}
          isSaving={isSaving}
          onSaveAndShareChanges={handleSaveAndShareChanges}
          disableEditing={disableEditing}
        />
      ),
    };
    setActiveDialogProps(draftDialogProps);
    setActiveDialogId(openDialog(draftDialogProps));
  }

  const handleCloseDraftDialog = () => {
    if (proposalId && !proposalId.isEmpty) {
      dispatch(clearUnsavedCommentsByProposalId(proposalId));
      dispatch(
        resetBuilderByProposalId({
          id: proposalId,
          session,
        }),
      );
    } else {
      dispatch(clearNewProposalBuilder());
    }

    navigate("/proposals/drafts", { replace: true });
    setActiveDialogId(undefined);
    setActiveDialogProps(undefined);
  };

  const handleSaveDraft = async (_saveAbleItem?: unknown, preventNavigation?: boolean) => {
    if (!updatedBuilderRef.current) throw new Error("Proposal builder is required to save a proposal.");
    setIsSaving(true);
    try {
      let unsavedDraftProposal: Proposal;
      let responseJSON: ProposalJSON;
      if (proposalId.isEmpty === false && dialogProposalRef.current) {
        responseJSON = await saveProposalQuery({
          session,
          updatedProposal: dialogProposalRef.current,
          proposalBuilderJSON: updatedBuilderRef.current.toJSON(),
        }).unwrap();
        dispatch(saveComments({ session }));
      } else {
        unsavedDraftProposal = updatedBuilderRef.current.buildNewDraft();
        responseJSON = await createProposalQuery({
          proposal: unsavedDraftProposal,
          session: session,
        }).unwrap();
      }
      if (!responseJSON.id) {
        throw new Error("Saved proposal is missing an ID");
      }
      enqueueSnackbar("Changes saved", { variant: "success" });

      if (responseJSON.id && !preventNavigation && !location.pathname.includes("/proposals/drafts/edit/")) {
        closeDialog();
        navigate(`/proposals/drafts/edit/${responseJSON.id}`, { replace: true });
      }
      return responseJSON.id;
    } catch (error) {
      enqueueSnackbar("Failed to save draft", { variant: "error" });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveRedlinedProposal = async (proposal: Proposal) => {
    if (!proposal?.redline) {
      console.error("No proposal to save");
      return;
    }
    if (!updatedBuilderRef.current) {
      console.error("No proposal builder to save redline changes");
      return;
    }

    try {
      setIsSaving(true);

      await saveProposalQuery({
        updatedProposal: proposal,
        proposalBuilderJSON: updatedBuilderRef.current?.toJSON(),
        session,
      }).unwrap();

      dispatch(saveComments({ session }));
      enqueueSnackbar("Changes saved", { variant: "success" });

      if (proposal.areReviewersModified) {
        enqueueSnackbar("Reviewers have been updated/invited", {
          variant: "success",
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to save proposal", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenActiveProposalDialog = useCallback((title: string, proposalId: Guid, readOnly?: boolean) => {
    const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
    const handleClose = () => {
      dispatch(
        resetProposalSessionByProposalId({
          id: proposalId,
        }),
      );
      dispatch(clearUnsavedCommentsByProposalId(proposalId));
      closeDialog();
      navigate(parentPath.pathname ?? "/proposals/active");
    };

    const redlineDialogProps: DialogProps = {
      title: title,
      titleStyle: { paddingBottom: 0 },
      MuiProps: { fullWidth: false, maxWidth: "xl" },
      contentSxProps: { pb: { xs: 0 } },
      resetStack: true,
      onClose: handleClose,
      showSaveButton: true,
      disableSave: true,
      onSave: async (saveAbleItem?: unknown, _preventNavigation?: boolean) => {
        if (saveAbleItem && saveAbleItem instanceof Proposal) {
          return handleSaveRedlinedProposal(saveAbleItem);
        }
        throw new Error("Invalid proposal to save");
      },
      component: (
        <ProposalRedlineDialog
          proposalId={proposalId}
          isSaving={isSaving}
          disableEditing={readOnly ?? false}
          onSaveAndShareChanges={handleSaveAndShareChanges}
        />
      ),
    };

    setActiveDialogProps(redlineDialogProps);
    setActiveDialogId(openDialog(redlineDialogProps));
  }, []);

  async function handleSaveAndShareChanges(args?: {
    proposal?: Proposal;
    confirmShare?: boolean;
  }): Promise<string | null | undefined> {
    const { proposal, confirmShare = true } = args ?? {};
    let proposalToSaveAndShare = proposal ?? dialogProposalRef.current;
    if (!proposalToSaveAndShare) {
      proposalToSaveAndShare = updatedBuilderRef.current?.buildNewDraft();
    } else if (!proposalToSaveAndShare.isSubmitted && updatedBuilderRef.current) {
      proposalToSaveAndShare = updatedBuilderRef.current.updateDraftProposal(proposalToSaveAndShare);
    }

    if (!proposalToSaveAndShare) {
      console.error("No proposal to save and share");
      enqueueSnackbar("There was a problem saving and sharing the proposal", { variant: "error" });
      return;
    }

    if (confirmShare) {
      const response = await confirm({
        title: "Share",
        message: "Are you sure you want to share any pending comments and/or changes for review?",
        okButtonText: "Share",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    const userCanShare = proposalToSaveAndShare.userCanShare(session.user);
    const proposalIsModified = proposalToSaveAndShare.isModified;

    if (areUnsavedCommentsRef.current) {
      await dispatch(publishComments({ session })).unwrap();
      enqueueSnackbar("Comments published.", { variant: "info" });
    } 
    if (!userCanShare) {
      enqueueSnackbar("You do not have permission to share this proposal", { variant: "warning" });
      return;
    }
    if (!proposalIsModified) {
      return proposalToSaveAndShare.id?.value;
    }

    const savedProposalJSON = await shareProposalQuery({ proposal: proposalToSaveAndShare, session }).unwrap();
    if (savedProposalJSON) {
      enqueueSnackbar("Proposal changes shared internally.", { variant: "success" });
    } else {
      console.error("Failed to share proposal changes", shareProposalResult.error);
      enqueueSnackbar("Failed to share proposal changes", { variant: "error" });
      return;
    }

    return savedProposalJSON?.id;
  }

  const handleBulkDraftProposalDelete = useCallback(async () => {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));

    const response = await confirm({
      title: "Delete Proposals?",
      message: `Are you sure you want to delete ${selectedRowIds.length} proposal(s)?`,
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    setRowSelection({});

    await bulkDeleteProposalsQuery({ ids: selectedRowIds.map((id) => id.value) });

    if (proposalsQueryParams) {
      dispatch(
        updateProposalInterfaceQuery({
          interfaceName: interfaceName,
          query: proposalsQueryParams,
        }),
      );
    }
    if (bulkDeleteProposalsResult.isError) {
      const error = bulkDeleteProposalsResult.error;
      console.error(error);
      enqueueSnackbar("Failed to delete proposals", { variant: "error" });
    }
  }, [dispatch, enqueueSnackbar, proposalsQueryParams]);

  const renderStatusColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue): ReactNode => {
      if (isProposalLoadIssue(proposal)) {
        return (
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "4000px",
                  whiteSpace: "normal",
                },
              },
            }}
            title={`Our staff has been notified. Reference id: ${proposal.id.value}`}
          >
            <span>Error Loading Proposal</span>
          </Tooltip>
        );
      }
      const userId = session.user?.id;
      let proposalStatus: string = ProposalStatus[proposal.status]?.toString();
      let displayStatus: string = HumanReadableProposalStatus[proposalStatus as keyof typeof ProposalStatus];
      const isClient =
        MaskedTeam.isPresentNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId.isEqualTo(userId);
      const isTeamLeader =
        MaskedTeam.isPresentNotMasked(proposal.vendorTeam) && proposal.vendorTeam?.leader?.userId.isEqualTo(userId);
      const isVendor = proposal.additionalVendors?.some((vendor) => vendor.userId.isEqualTo(userId));

      if (
        (proposal.status === ProposalStatus.AwaitingApprovalByClient && isClient) ||
        (proposal.status === ProposalStatus.AwaitingApprovalByTeamLeader && isTeamLeader) ||
        (proposal.status === ProposalStatus.AwaitingApprovalByVendors && isVendor)
      )
        displayStatus = "Awaiting Your Approval";

      // If the proposal is awaiting review by the user
      if (
        proposal.clientReviewers?.some(
          (reviewer) => session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved,
        ) ||
        proposal.vendorReviewers?.some(
          (reviewer) => session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved,
        )
      ) {
        displayStatus = "Awaiting Your Review";
      }

      return displayStatus;
    },
    [session.user?.id],
  );

  const renderCreatorColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal) || !proposal.creator) return <Icon>remove</Icon>;
      if (proposal.creator.userId.isEqualTo(session.user?.id)) {
        return "You";
      }
      return <CreatorLink creator={proposal.creator} />;
    },
    [session.user?.id],
  );

  const renderTeamColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal) || !proposal.vendorTeam) return <Icon>remove</Icon>;
      return <VendorTeamLink proposal={proposal} session={session} />;
    },
    [session.user?.id],
  );

  const renderFeeScheduleColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal) || !proposal.feeSchedule) return <Icon>remove</Icon>;
      return <FeeScheduleLink proposal={proposal} />;
    },
    [session.user?.id],
  );

  const statusFilterOptions = useMemo(
    () =>
      Object.entries(HumanReadableProposalStatus).map(([key, value]) => ({
        value: key,
        label: value,
      })),
    [],
  );

  const columnHelper = createMRTColumnHelper<Proposal | ProposalLoadIssue>();
  // TODO: reference proposal builders for column values if they exist
  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        Cell: ({ row }) => renderStatusColumn(row.original),
      }),
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: NameCell,
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        Cell: DescriptionCell,
      }),
      columnHelper.display({
        header: "Creator",
        id: "creator",
        Cell: ({ row }) => renderCreatorColumn(row.original),
      }),
      columnHelper.display({
        header: "Negotiable",
        id: "negotiable",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: "Negotiable" },
          { value: "false", label: "Non-negotiable" },
        ],
        Cell: NegotiableCell,
      }),
      columnHelper.display({
        header: "Team",
        id: "team",
        size: 50,
        Cell: ({ row }) => renderTeamColumn(row.original),
      }),
      columnHelper.display({
        header: "Fee Schedule",
        id: "feeSchedule",
        size: 50,
        Cell: ({ row }) => renderFeeScheduleColumn(row.original),
      }),
      columnHelper.display({
        header: "Response Due By",
        id: "responseDueBy",
        size: 50,
        Cell: ResponseDueByCell,
      }),
      columnHelper.display({
        header: "Start Date",
        id: "startDate",
        size: 50,
        Cell: StartDateCell,
      }),
      columnHelper.display({
        header: "End Date",
        id: "endDate",
        size: 50,
        Cell: EndDateCell,
      }),
      columnHelper.display({
        header: "Hire Date",
        id: "hireDate",
        size: 50,
        Cell: HireDateCell,
      }),
      columnHelper.display({
        header: "Archive Date",
        id: "archiveDate",
        size: 50,
        Cell: ArchiveDateCell,
      }),
    ],
    [activeTab, renderCreatorColumn, renderTeamColumn, renderFeeScheduleColumn, renderStatusColumn],
  );

  const openProposal = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal)) {
        console.warn("Cannot open proposal with loading issues.");
        return;
      }
      if (!proposal.id) {
        console.error("Proposal ID is required");
        return;
      }
      const editable = proposal.availableActions.Edit.some((userId) => userId.isEqualTo(session.user?.id));
      const revisable = proposal.availableActions.Revise.some((userId) => userId.isEqualTo(session.user?.id));
      const reviewable = proposal.availableActions.Review.some((userId) => userId.isEqualTo(session.user?.id));
      const submittable = proposal.availableActions.Submit.some((userId) => userId.isEqualTo(session.user?.id));

      let tab: PageTab = activeTab;
      let action: "view" | "edit" | "revise" | "review" = "view";

      if (editable && !revisable) {
        tab = PageTab.Drafts;
        action = "edit";
      }
      if (revisable) {
        if (submittable) {
          tab = PageTab.Drafts;
        } else {
          tab = PageTab.Active;
        }
        action = "revise";
      }
      if (reviewable) {
        action = "review";
      }

      navigate(`/proposals/${tab}/${action}/${proposal.id}`);
    },
    [activeTab, navigate, session.user?.id],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => {
      return <ProposalToolbar table={table} activeTab={activeTab} />;
    },
    [activeTab],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => (
      <>
        {!isMediumDisplaySize && (
          <ProposalToolbarButtonsContainer>
            <ProposalToolbarButtons>
              <ToggleGlobalFilterButton table={props.table} />
              <ToggleFiltersButton table={props.table} />
              <ShowHideColumnsButton table={props.table} />
              <ToggleDensePaddingButton table={props.table} />
              <ToggleFullScreenButton table={props.table} />
            </ProposalToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              onClick={() => {
                if (routeParams.action === "create") {
                  handleCreateDraftProposal();
                } else {
                  navigate("/proposals/drafts/create");
                }
              }}
            >
              Create Proposal
            </Button>
          </ProposalToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate("/proposals/drafts/create")}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    [isMediumDisplaySize, navigate, routeParams.action, handleCreateDraftProposal],
  );

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<Proposal | ProposalLoadIssue> }) => ({
      onClick: () => openProposal(row.original),
      sx: {
        backgroundColor: isProposalLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" // light red for error
          : "inherit", // default color
        cursor: isProposalLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openProposal],
  );

  const muiTableContainerProps = useCallback(
    ({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => ({
      sx: {
        height:
          `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px -` +
          ` ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    [],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (proposalsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    return {
      action: (
        <ProposalAlertActions>
          <Button color="primary" startIcon={<DeleteIcon />} onClick={handleBulkDraftProposalDelete}>
            Delete
          </Button>
        </ProposalAlertActions>
      ),
    };
  }, [proposalsAPIQuery.isError, handleBulkDraftProposalDelete]);

  const table = useMaterialReactTable({
    columns,
    data: tableProposals ?? [],
    enableRowSelection: activeTab === PageTab.Drafts,
    enableRowActions: false,
    enableTableHead: true,
    muiTableBodyRowProps,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps,
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps,
    rowCount: tableProposals?.length ?? 0,
    pageCount: Math.ceil((tableProposals?.length ?? 0) / pagination.pageSize),
    enablePagination: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility: {
        hireDate: activeTab === PageTab.Archived,
        archiveDate: activeTab === PageTab.Archived,
      },
      columnFilters,
      globalFilter,
      isLoading: proposalsAPIQuery.isLoading,
      pagination,
      showAlertBanner: Boolean(proposalsAPIQuery.isError),
      showProgressBars: proposalsAPIQuery.isLoading,
      sorting,
      rowSelection,
      showSkeletons: proposalsAPIQuery.isLoading,
    },
  });

  if (!session.user?.legalEntityId) {
    return <NotAuthorized />;
  }

  return <MaterialReactTable table={table} />;
}

const NameCell = ({ row }: { row: { original: Proposal | ProposalLoadIssue } }) => {
  const value = !isProposalLoadIssue(row.original) && row.original.name?.value;
  return value ? <RowTitle>{value}</RowTitle> : <Icon>remove</Icon>;
};
const DescriptionCell = ({ row }: { row: { original: Proposal | ProposalLoadIssue } }) => {
  const value = !isProposalLoadIssue(row.original) && row.original.description?.value;
  return value ? <Typography variant="body2">{value}</Typography> : <Icon>remove</Icon>;
};
const NegotiableCell = ({ row }: { row: { original: Proposal | ProposalLoadIssue } }) => {
  let value: boolean | undefined;
  if (!isProposalLoadIssue(row.original)) {
    value = row.original.negotiable?.value;
  }
  if (value === undefined) {
    return <Icon>remove</Icon>;
  }
  return value ? <CheckIcon /> : <CloseIcon />;
};

function ResponseDueByCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.responseDueBy;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function StartDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.startDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function EndDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.endDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function HireDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.hireDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function ArchiveDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.archiveDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}
